import React, { useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from "../../contexts/AuthContext.jsx";
import { useEnvironment } from '../../contexts/EnvironmentContext';
import HomeNavMenu from './HomeNavMenu';
import Breadcrumbs from './Breadcrumbs.jsx';
import NotificationBell from '../Notifications/NotificationBell';
import GlobalSearch from '../Search/GlobalSearch';
import { getVersion } from '../../services/versionService.js';
import { isLocalDevLayoutActive } from '../../utils/localDevLayout.js';
import { isParticipantDomain } from '../../utils/participantDomain.js';
import { usesStaffShell, sectionLabelFor } from '../../utils/appShell.js';


const SIDEBAR_COLLAPSED_KEY = 'flexcoa-sidebar-collapsed';

const Layout = ({ children, isDev }) => {
  const { isAuthenticated, setIsAuthenticated, userType, user } = useContext(AuthContext);
  const { isDemo } = useEnvironment();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [version, setVersion] = useState(null);
  const [isScreenBuilder, setIsScreenBuilder] = useState(false);
  const [isLocalDevMode, setIsLocalDevMode] = useState(() => isLocalDevLayoutActive());
  // The staff sidebar can collapse to an icon rail; remember the choice.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggleSidebar = () => {
    setSidebarCollapsed((collapsed) => {
      const next = !collapsed;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // Private mode or blocked storage: the choice just isn't remembered.
      }
      return next;
    });
  };
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);
  
  // Stay aligned with LocalDevBanner + screen builder (same rules as isLocalDevLayoutActive)
  useEffect(() => {
    const sync = () => setIsLocalDevMode(isLocalDevLayoutActive());

    sync();

    const observer = new MutationObserver(sync);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const isParticipant = userType === 'participant';
  const isSponsorSelector = location.pathname === '/sponsor-selector';
  // Caregiver-facing routes are unauthenticated-or-caregiver-token surfaces and
  // share none of the staff chrome (no sidebar, breadcrumbs, header, footer).
  const isCaregiverRoute =
    location.pathname.startsWith('/caregiver-login') ||
    location.pathname.startsWith('/caregiver/');
  const noSidebarPaths = ['/', '/login', '/forgot-password', '/register', '/sponsor-selector'];
  const noBreadcrumbPaths = ['/reset-password', '/profile', '/development', '/caregiver-login', '/caregiver'];
  const noHeaderPaths = ['/login', '/forgot-password', '/reset-password', '/caregiver-login', '/caregiver', '/mfa/recovery', '/auth/activate'];
  // Sign-in pages draw their own full-screen layout (AuthShell, FECOA-2174).
  const authShellPaths = ['/login', '/forgot-password', '/reset-password', '/mfa/recovery', '/auth/activate', '/caregiver-login', '/caregiver/first-login', '/caregiver/invite'];
  const isParticipantPortal = location.pathname.startsWith('/home/') || (location.pathname === '/' && isParticipantDomain());
  const isAuthShell = isParticipantPortal || authShellPaths.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
  // The signed-out landing page draws full-width bands under the header.
  const isLandingPage = !isAuthenticated && !isParticipantPortal && (location.pathname === '/' || location.pathname === '/home');
  const shouldShowSidebar = isAuthenticated && !isParticipant && !isCaregiverRoute && !noSidebarPaths.includes(location.pathname);
  const shouldRenderBreadcrumbs = !pathnames.includes('auth') && !isCaregiverRoute;
  const isStudiesRootPath = location.pathname === '/studies';
  const isCompletedSurveysPath = location.pathname.startsWith('/completed-surveys');
  const isShallowPath = pathnames.length <= 1;
  // Assessment builder detail route (/assessment-builder/:assessmentId) only shows
  // a raw UUID in the breadcrumb and flashes during the builder's load spinner, so
  // suppress breadcrumbs there entirely.
  const isAssessmentBuilderDetailPath = pathnames[0] === 'assessment-builder' && pathnames.length > 1;
  // Review routes (read-only approval surface). The naive path-segment breadcrumb
  // turns 'review' into a bogus study-name link that loads a "review" draft and
  // errors, so suppress breadcrumbs on the whole review flow.
  const isReviewPath =
    (pathnames[0] === 'studybuilder' || pathnames[0] === 'assessment-builder') &&
    (pathnames[1] === 'review' || pathnames[1] === 'review-queue');
  // Audit-history page (/review-history/:entityType/:objectId). Every path
  // segment ('review-history', 'assessment', the raw object id) links nowhere
  // real, so suppress breadcrumbs and let the page's own Back button navigate.
  const isReviewHistoryPath = pathnames[0] === 'review-history';

  // Check for screenbuilder-active class on body
  useEffect(() => {
    const checkScreenBuilderClass = () => {
      setIsScreenBuilder(document.body.classList.contains('screenbuilder-active'));
    };
    
    // Check immediately
    checkScreenBuilderClass();
    
    // Set up a MutationObserver to watch for class changes
    const observer = new MutationObserver(checkScreenBuilderClass);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      // Using async function to fetch API version
      const fetchVersion = async () => {
        try {
          const apiVersion = await getVersion();
          setVersion(apiVersion);
        } catch (error) {
          console.error('Error fetching version:', error);
          setVersion(null);
        }
      };
      
      fetchVersion();
    } else {
      setVersion(null);
    }
  }, [isAuthenticated]);

  // When local dev banner is visible (32px), increase top padding to account for it.
  // Caregiver routes own their own layout end-to-end (including absorbing the
  // local-dev banner via their own pt-8 styling), so Layout adds no padding.
  const topPadding = isAuthShell
    ? (isLocalDevMode ? 'pt-8' : '')
    : isCaregiverRoute
    ? ''
    : isScreenBuilder
    ? (isLocalDevMode ? 'pt-8' : '')
    : (isLocalDevMode ? 'pt-24' : 'pt-16');

  const participantPortalForceLight =
    location.pathname === '/login/participant' ||
    location.pathname.startsWith('/home/') ||
    (location.pathname === '/' && isParticipantDomain());

  const showBreadcrumbs = shouldShowSidebar && !isSponsorSelector && shouldRenderBreadcrumbs && !isStudiesRootPath && !isCompletedSurveysPath && !isShallowPath && !isAssessmentBuilderDetailPath &&
    !isReviewPath && !isReviewHistoryPath &&
    !noBreadcrumbPaths.some(path => location.pathname.startsWith(path));

  // Staff pages: sidebar on the left, the page on a white panel (FECOA-2174).
  // The screen builder covers the whole window, so the shell steps aside.
  const showStaffShell =
    !isScreenBuilder && !isAuthShell && !isCaregiverRoute &&
    usesStaffShell({ isAuthenticated, userType, pathname: location.pathname });
  const showHeader = !isScreenBuilder && !isAuthShell && !noHeaderPaths.some(path => location.pathname.startsWith(path));

  const rootPadding = showStaffShell ? (isLocalDevMode ? 'pt-8' : '') : topPadding;
  const rootSurface = showStaffShell
    ? 'bg-canvas'
    : isParticipant ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-900';
  const mainClassName = showStaffShell
    ? 'mx-auto w-full max-w-[1400px] px-4 pb-12 pt-6 sm:px-6 lg:px-8 lg:pt-7'
    : isScreenBuilder || isAuthShell || isLandingPage || isCaregiverRoute ? '' : 'max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8';

  // One element tree for every kind of page. Switching between the shell and
  // full-screen pages (such as the screen builder) only changes classes, so the
  // page inside is never unmounted and remounted.
  return (
    <div
      className={`min-h-screen ${rootSurface} relative ${rootPadding} theme-transition ${participantPortalForceLight ? 'force-light-mode' : ''}`}
      style={{ '--app-sidebar-width': showStaffShell ? (sidebarCollapsed ? '68px' : '248px') : '0px' }}
    >
      {/* Accessibility: skip-to-content link */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {showHeader && (
        <HomeNavMenu
          isAuthenticated={isAuthenticated}
          setIsAuthenticated={setIsAuthenticated}
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
          isDev={isDev}
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={toggleSidebar}
        />
      )}

      <div className={showStaffShell ? 'transition-[padding] duration-200 ease-out lg:pl-[var(--app-sidebar-width)]' : ''}>
        <div className={showStaffShell ? 'lg:py-2 lg:pr-2' : ''}>
          <div
            className={
              showStaffShell
                ? `min-h-screen bg-gray-50 dark:bg-gray-900 lg:overflow-hidden lg:rounded-xl lg:border lg:border-gray-200/80 lg:shadow-panel lg:dark:border-white/[0.07] ${
                    isLocalDevMode ? 'lg:min-h-[calc(100vh-3rem)]' : 'lg:min-h-[calc(100vh-1rem)]'
                  }`
                : ''
            }
          >
            {showStaffShell && (
              <div className="flex h-14 items-center justify-between gap-4 border-b border-gray-200/80 bg-white px-4 sm:px-6 lg:px-8 dark:border-white/[0.06] dark:bg-gray-900">
                <div className="min-w-0 flex-1">
                  {showBreadcrumbs ? (
                    <Breadcrumbs />
                  ) : (
                    <span className="truncate text-[13px] font-medium text-gray-900 dark:text-gray-100">
                      {sectionLabelFor(location.pathname)}
                    </span>
                  )}
                </div>
                <div className="flex flex-shrink-0 items-center gap-1.5 sm:gap-2">
                  <GlobalSearch isDev={isDev} />
                  <NotificationBell />
                </div>
              </div>
            )}

            {!showStaffShell && !isScreenBuilder && showBreadcrumbs && (
              <div className="max-w-7xl mx-auto px-4 pt-5 sm:px-6 lg:px-8">
                <Breadcrumbs />
              </div>
            )}

            <main id="main-content" role="main" className={mainClassName}>
              <div className="flex-grow">{children}</div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;
